<template>
  <v-container fluid class="pa-0 title-card-wrapper">
    <v-card
      v-if="hasData"
      v-intersect="onIntersect"
      flat
      class="title-card"
      :class="{ 'title-card--poster': showPosterFrame }"
      :style="getSizeStyle"
      @click="onTitleDetails(item.ncTitleId)"
    >
      <div v-if="showPosterFrame" class="title-card-poster">
        <img
          v-if="posterPath"
          :src="getPosterSrc"
          :srcset="getPosterSrcset"
          :sizes="getPosterSizes"
          alt=""
          loading="lazy"
          @error="posterFailed = true"
        />
      </div>
      <div class="title-card-body">
      <div class="title-card-header">
        <div class="title-card-name" :title="getFullTitle">
          {{ getShortTitle }}<span v-if="item.year" class="title-card-year"> ({{ item.year }})</span>
        </div>
        <div v-if="getTypeLabel || getRating" class="title-card-meta">
          <span v-if="getTypeLabel" class="title-card-type">{{ getTypeLabel }}</span>
          <span
            v-if="getRating"
            class="title-card-rating"
            :class="getRatingClass"
            :title="getRatingTooltip"
          >★ {{ getRating }}</span>
        </div>
      </div>
      <!-- Text cards only. Fetched credits fade in over the placeholder; credits already on hand show without one. -->
      <transition name="title-card-fade">
        <div v-if="showPosterFrame" key="none"></div>
        <div v-else-if="getDirectors || getActors" key="credits" class="title-card-credits">
          <div v-if="getDirectors" class="title-card-credit">
            <span class="title-card-label">Dir.</span> {{ getDirectors }}
          </div>
          <div v-if="getActors" class="title-card-credit">
            <span class="title-card-label">With</span> {{ getActors }}
          </div>
        </div>
        <!-- Placeholder lines while credits load, so the card doesn't jump when they arrive. -->
        <div v-else-if="waitingFor" key="skeleton" class="title-card-credits" aria-hidden="true">
          <div class="title-card-skeleton"></div>
          <div class="title-card-skeleton title-card-skeleton--short"></div>
        </div>
      </transition>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import { Intersect } from 'vuetify/lib/directives'
import { getCachedCredits, releaseCredits, requestCredits } from '../../../services/titleCredits'
import { getPosterPath, loadPosters, posterSrc, posterSrcset, postersLoaded } from '../../../services/titlePosters'
import { getTitleTypeLabel } from '../../../utils/titleType'

// Shows the title's TMDB poster. A title with no poster, or whose image fails to load, gets a
// text card instead. List items from the data API carry title and year but no credits, so a
// text card fetches directors/actors once it scrolls into view.
const MAX_DIRECTORS = 1
const MAX_ACTORS = 2
// Long titles are shortened in code rather than CSS-clamped, so the year after them stays visible.
const MAX_TITLE_CHARS = 48

const joinNames = (list, max) => (list || []).slice(0, max).map((x) => x.name).join(', ')

// Each title gets a stable hue from its id, so a row of cards varies the way posters did
// and a title keeps the same color wherever it appears.
// FNV-1a plus a final mix, so sequential ids still land far apart on the color wheel.
const hueFor = (id) => {
  let hash = 2166136261
  for (const ch of `${id}`) hash = Math.imul(hash ^ ch.charCodeAt(0), 16777619)
  hash = Math.imul(hash ^ (hash >>> 16), 0x45d9f3b)
  hash ^= hash >>> 16
  return (hash >>> 0) % 360
}

export default {
  props: { item: Object, height: Number, width: Number },
  directives: { Intersect },
  data: () => ({
    credits: null,
    isVisible: false,
    // Title id of a credits request this card is still waiting on.
    waitingFor: null,
    postersReady: postersLoaded(),
    posterFailed: false,
  }),
  methods: {
    onTitleDetails(ncTitleId) {
      this.$router.safePush(`/title/${ncTitleId}`)
    },
    onIntersect(entries, observer, isIntersecting) {
      this.isVisible = isIntersecting
      if (isIntersecting) {
        this.loadCredits()
      } else {
        // Let a queued request that hasn't started yet be dropped.
        this.stopWaiting()
      }
    },
    async loadCredits() {
      const ncTitleId = this.item?.ncTitleId
      if (this.showPosterFrame || !ncTitleId || this.hasOwnCredits || this.credits || this.waitingFor === ncTitleId) return

      const cached = getCachedCredits(ncTitleId)
      if (cached) {
        this.credits = cached
        return
      }
      this.waitingFor = ncTitleId
      const credits = await requestCredits(ncTitleId)
      if (this.waitingFor === ncTitleId) {
        this.waitingFor = null
        this.credits = credits
      }
    },
    stopWaiting() {
      if (this.waitingFor) {
        releaseCredits(this.waitingFor)
        this.waitingFor = null
      }
    },
  },
  watch: {
    // Lists may reuse a card for a different title.
    'item.ncTitleId'(ncTitleId) {
      this.stopWaiting()
      this.posterFailed = false
      this.credits = ncTitleId ? getCachedCredits(ncTitleId) : null
      if (this.isVisible) this.loadCredits()
    },
    // The poster map turned out to have nothing for this title, or its image failed.
    showPosterFrame(show) {
      if (!show && this.isVisible) this.loadCredits()
    },
  },
  async mounted() {
    await loadPosters()
    this.postersReady = true
  },
  beforeDestroy() {
    this.stopWaiting()
  },
  computed: {
    hasData() {
      return !!this.item?.ncTitleId
    },
    posterPath() {
      if (!this.postersReady || this.posterFailed) return null
      return getPosterPath(this.item?.ncTitleId)
    },
    // Also true while the map loads, so a card doesn't start as text and then change.
    showPosterFrame() {
      return !this.postersReady || !!this.posterPath
    },
    getPosterSrc() {
      return posterSrc(this.posterPath)
    },
    getPosterSrcset() {
      return posterSrcset(this.posterPath)
    },
    // Card widths from the row sizes in TitleHorizontalList, or a fixed width where one is passed.
    getPosterSizes() {
      if (this.width) return `${this.width}px`
      return '(max-width: 480px) 50vw, (max-width: 768px) 34vw, (max-width: 1024px) 20vw, (max-width: 1400px) 17vw, 12.5vw'
    },
    getShortTitle() {
      const title = this.item?.title || ''
      return title.length > MAX_TITLE_CHARS ? `${title.slice(0, MAX_TITLE_CHARS - 1).trimEnd()}…` : title
    },
    getFullTitle() {
      return this.item?.year ? `${this.item.title} (${this.item.year})` : this.item?.title
    },
    // Movies are the default, so only other types (Series, Documentary) get a label.
    getTypeLabel() {
      if (!this.item?.type) return ''
      const label = getTitleTypeLabel(this.item.type)
      return label === 'Movie' ? '' : label
    },
    hasOwnCredits() {
      return Array.isArray(this.item?.directorNameList) || Array.isArray(this.item?.actorNameList)
    },
    getDirectors() {
      const list = this.hasOwnCredits ? this.item.directorNameList : this.credits?.directorNameList
      return joinNames(list, MAX_DIRECTORS)
    },
    getActors() {
      const list = this.hasOwnCredits ? this.item.actorNameList : this.credits?.actorNameList
      return joinNames(list, MAX_ACTORS)
    },
    // Crowd rating is on a 5-point scale; a missing or zero rating isn't shown.
    getRating() {
      const rating = parseFloat(this.item?.crowdRating)
      return rating > 0 ? rating.toFixed(1) : ''
    },
    getRatingClass() {
      const rating = parseFloat(this.getRating)
      if (rating >= 4) return 'title-card-rating--high'
      if (rating >= 3) return 'title-card-rating--mid'
      return 'title-card-rating--low'
    },
    getRatingTooltip() {
      const size = parseFloat(this.item?.crowdSize)
      const sizeText = size > 0 ? `, crowd size ${size.toFixed(1)} / 5` : ''
      return `Crowd rating ${this.getRating} / 5${sizeText}`
    },
    getSizeStyle() {
      const style = { '--card-hue': hueFor(this.item?.ncTitleId) }
      if (this.width) style.width = `${this.width}px`
      if (this.height) style.minHeight = `${this.height}px`
      return style
    },
  },
}
</script>

<style scoped>
/* Fill the list item so every card in a row matches the tallest one. */
.title-card-wrapper {
  height: 100%;
}

/* Theme colors. The dark card is a step lighter than the app's #272727 page background.
   --card-hue is set per title in getSizeStyle; the accent and top wash are derived from it. */
.title-card.theme--light {
  --card-bg: #ffffff;
  --card-border: rgba(0, 0, 0, 0.12);
  --card-divider: rgba(0, 0, 0, 0.08);
  --card-accent: hsl(var(--card-hue, 4), 58%, 36%);
  --card-wash: hsla(var(--card-hue, 4), 75%, 50%, 0.16);
  --card-on-accent: #ffffff;
  --rating-high: #1b7a3d;
  --rating-mid: #9a6200;
  --rating-low: rgba(0, 0, 0, 0.55);
  --card-shadow: 0 1px 2px rgba(0, 0, 0, 0.08), 0 2px 6px rgba(0, 0, 0, 0.06);
  --card-shadow-hover: 0 4px 10px rgba(0, 0, 0, 0.12), 0 10px 24px rgba(0, 0, 0, 0.1);
  --card-muted: rgba(0, 0, 0, 0.6);
  --card-skeleton: rgba(0, 0, 0, 0.08);
}

.title-card.theme--dark {
  --card-bg: #333333;
  --card-border: rgba(255, 255, 255, 0.1);
  --card-divider: rgba(255, 255, 255, 0.08);
  --card-accent: hsl(var(--card-hue, 4), 62%, 62%);
  --card-wash: hsla(var(--card-hue, 4), 70%, 60%, 0.16);
  --card-on-accent: rgba(0, 0, 0, 0.85);
  --rating-high: #6fd492;
  --rating-mid: #f2b84b;
  --rating-low: rgba(255, 255, 255, 0.6);
  --card-shadow: 0 1px 2px rgba(0, 0, 0, 0.4), 0 2px 6px rgba(0, 0, 0, 0.3);
  --card-shadow-hover: 0 4px 10px rgba(0, 0, 0, 0.5), 0 10px 24px rgba(0, 0, 0, 0.4);
  --card-muted: rgba(255, 255, 255, 0.65);
  --card-skeleton: rgba(255, 255, 255, 0.1);
}

.title-card {
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
  min-height: 120px;
  height: 100%;
  overflow: hidden;
  cursor: pointer;
  background-color: var(--card-bg) !important;
  background-image: linear-gradient(to bottom, var(--card-wash), transparent 64px);
  border: 1px solid var(--card-border) !important;
  border-left: 4px solid var(--card-accent) !important;
  border-radius: 8px !important;
  box-shadow: var(--card-shadow) !important;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.title-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--card-shadow-hover) !important;
}

.title-card:focus-visible {
  outline: 2px solid var(--card-accent);
  outline-offset: 2px;
}

.title-card-body {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  padding: 12px 14px 12px 12px;
}

.title-card--poster {
  background-image: none;
  border-left: 1px solid var(--card-border) !important;
}

.title-card--poster .title-card-body {
  padding: 8px 10px 10px;
}

.title-card--poster .title-card-header {
  margin-bottom: 0;
}

.title-card--poster .title-card-name {
  font-size: 15px;
}

.title-card-poster {
  flex: none;
  aspect-ratio: 2 / 3;
  background-color: var(--card-skeleton);
}

.title-card-poster img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.title-card-header {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  margin-bottom: 10px;
}

/* Oswald (loaded in public/index.html) gives titles a poster feel, and being condensed
   it fits more of a long title per line. */
.title-card-name {
  font-family: 'Oswald', 'Roboto', sans-serif;
  font-size: 17px;
  font-weight: 500;
  letter-spacing: 0.01em;
  line-height: 1.25;
  word-break: break-word;
}

.title-card-year {
  font-weight: 400;
  color: var(--card-muted);
}

.title-card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
}

.title-card-type {
  padding: 1px 6px;
  border-radius: 4px;
  background-color: var(--card-accent);
  color: var(--card-on-accent);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.title-card-rating {
  padding: 0 6px;
  border: 1px solid currentColor;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  line-height: 16px;
  white-space: nowrap;
}

.title-card-rating--high {
  color: var(--rating-high);
}

.title-card-rating--mid {
  color: var(--rating-mid);
}

.title-card-rating--low {
  color: var(--rating-low);
}

/* Credits sit at the bottom, under a hairline, so cards in a row line up. */
.title-card-credits {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: auto;
  padding-top: 8px;
  border-top: 1px solid var(--card-divider);
}

.title-card-credit {
  display: -webkit-box;
  overflow: hidden;
  font-size: 12px;
  line-height: 1.35;
  color: var(--card-muted);
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.title-card-label {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}

.title-card-skeleton {
  height: 10px;
  margin: 2px 0;
  border-radius: 4px;
  background-color: var(--card-skeleton);
}

.title-card-skeleton--short {
  width: 60%;
}

/* Only the entering credits animate; the placeholder leaves at once so they never stack. */
.title-card-fade-enter-active {
  transition: opacity 0.35s ease, transform 0.35s ease;
}

.title-card-fade-enter {
  opacity: 0;
  transform: translateY(3px);
}

@media (prefers-reduced-motion: reduce) {
  .title-card-fade-enter-active {
    transition: none;
  }
}
</style>
